/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useIndustryConfig } from "@/hooks/config/use-industry-config";
import type { IndustryConfig as IndustryConfigValue } from "@/lib/config/industry-config";
import type { IndustryType } from "@/lib/constants/industries";
import type { IndustryTerminology } from "@/lib/constants/terminology";
import { logger } from "@/lib/utils/logger";
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface IndustryContextValue extends IndustryConfigValue {
  setTerminologyOverride: (terminology: IndustryTerminology | null) => void;
  setIndustryOverride: (industryType: IndustryType | null) => void;
}

const IndustryContext = createContext<IndustryContextValue | null>(null);

// Test mode detection - same logic as OrganizationProvider
const isTestModeActive = (): boolean => {
  // Environment-based check (works in SSR)
  const envTestMode =
    process.env["NEXT_PUBLIC_E2E_TEST_MODE"] === "true" ||
    process.env["E2E_TEST_MODE"] === "true" ||
    process.env["NODE_ENV"] === "test";

  // Client-side check (only if window is available)
  if (typeof window !== "undefined") {
    return envTestMode || Boolean(window.__TEST_BYPASS_ENABLED);
  }

  return envTestMode;
};

// Internal provider that uses Clerk hooks
function IndustryProviderInternal({ children }: { children: ReactNode }) {
  const [terminologyOverride, setTerminologyOverride] =
    useState<IndustryTerminology | null>(null);
  const [industryOverride, setIndustryOverride] = useState<IndustryType | null>(
    null,
  );

  const config = useIndustryConfig({
    industryType: industryOverride,
    terminologyOverride: terminologyOverride ?? undefined,
  });

  const contextValue = useMemo<IndustryContextValue>(() => {
    return {
      ...config,
      setTerminologyOverride,
      setIndustryOverride,
    };
  }, [config]);

  return (
    <IndustryContext.Provider value={contextValue}>
      {children}
    </IndustryContext.Provider>
  );
}

// Test mode provider that doesn't use Clerk hooks
function IndustryProviderTestMode({ children }: { children: ReactNode }) {
  const [terminologyOverride, setTerminologyOverride] =
    useState<IndustryTerminology | null>(null);
  const [industryOverride, setIndustryOverride] = useState<IndustryType | null>(
    null,
  );

  // In test mode, don't call useIndustryConfig (it uses Clerk hooks)
  // Instead, create config directly
  const config = useMemo(() => {
    try {
      // Use static import instead of require() to avoid potential errors
      const {
        getIndustryConfig,
        resolveIndustryType,
      } = require("@/lib/config/industry-config");
      const industryType = industryOverride ?? resolveIndustryType(undefined);
      const baseConfig = getIndustryConfig(industryType);

      if (terminologyOverride) {
        return {
          ...baseConfig,
          terminology: terminologyOverride,
        };
      }

      return baseConfig;
    } catch (error) {
      // Fallback to default config if import fails
      logger.warn(
        "[IndustryProviderTestMode] Failed to load industry config, using defaults",
        {
          error: error instanceof Error ? error.message : String(error),
        },
      );
      // Return a minimal default config
      return {
        industryType: "general_contractor" as const,
        terminology: {
          client: "Client",
          project: "Project",
          estimate: "Estimate",
          proposal: "Proposal",
          jobsite: "Jobsite",
          service: "Service",
        },
      };
    }
  }, [industryOverride, terminologyOverride]);

  const contextValue = useMemo<IndustryContextValue>(() => {
    return {
      ...config,
      setTerminologyOverride,
      setIndustryOverride,
    };
  }, [config]);

  return (
    <IndustryContext.Provider value={contextValue}>
      {children}
    </IndustryContext.Provider>
  );
}

// Public provider that conditionally renders based on test mode
export function IndustryProvider({ children }: { children: ReactNode }) {
  // Check test mode using environment variables (works in SSR and client)
  // This MUST be checked before any Clerk hooks are called
  const testModeActive = isTestModeActive();

  // In test mode, render test-only provider (no Clerk hooks)
  if (testModeActive) {
    return <IndustryProviderTestMode>{children}</IndustryProviderTestMode>;
  }

  // In normal mode, render provider that uses Clerk hooks
  // This is safe because ClerkProvider wraps this in RootProviders
  return <IndustryProviderInternal>{children}</IndustryProviderInternal>;
}

export function useIndustry(): IndustryContextValue {
  const context = useContext(IndustryContext);
  if (!context) {
    throw new Error("useIndustry must be used within an IndustryProvider");
  }
  return context;
}
